import "./ClientesPage.css";
import { ClienteService } from "../../services/terceros/ClienteService";
import { ClienteDetalleResponse } from "../../../shared/response/terceros/ClienteDetalleResponse";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { FincaFichaResponse } from "../../../shared/response/terceros/FincaFichaResponse";
import { storageSubidaService } from "../../../shared/services/storage/StorageSubidaService";
import { OrtomosaicoEstado } from "../../../shared/response/terceros/FincaResponse";
import { FincaResumenResponse } from "../../../shared/response/terceros/FincaResumenResponse";
import { ClienteDispositivoResponse } from "../../../shared/response/terceros/ClienteDispositivoResponse";
import { OrtomosaicoWatchService } from "../../services/terceros/OrtomosaicoWatchService";
import { FincaGetByIdService } from "../../../shared/services/terceros/FincaGetByIdService";
import { FincaDeleteService } from "../../services/terceros/FincaDeleteService";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { ClienteFormModal } from "./ClienteFormModal";
import { FincaFormModal } from "./FincaFormModal";
import { AsignarPlanModal } from "./AsignarPlanModal";
import { GuardarOrtomosaicoService } from "../../services/terceros/GuardarOrtomosaicoService";
import { GuardarConteoService } from "../../services/terceros/GuardarConteoService";
import { GuardarAoiService } from "../../services/terceros/GuardarAoiService";
import { GuardarLotesService } from "../../services/terceros/GuardarLotesService";
import { UsuarioAppResponse } from "../../../shared/response/seguridad/UsuarioAppResponse";
import { UsuarioAppListByClienteIdService } from "../../../shared/services/seguridad/UsuarioAppListByClienteIdService";
import { UsuarioAppFormModal } from "./UsuarioAppFormModal";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { NotFoundError } from "../../../shared/services/api/error/NotFoundError";
import type { Polygon } from "geojson";
import { countPolygonVertices, downloadKml, kmlFileName, multiPointToKml, polygonToKml, polygonsToKml } from "../../../shared/utils/kmlUtils";
import { formatInt } from "../../../shared/utils/formatUtils";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";
import { FincaGisMapModal } from "./FincaGisMapModal";
import { GpsTrackerDescargaModal } from "../hardware/GpsTrackerDescargaModal";

// ── Constants ──

const AVATAR_COLORS = [
  "#0095E2", "#2EB054", "#8B5CF6", "#EF9F27", "#E24B4A",
  "#0EA5E9", "#F59E0B", "#10B981", "#6366F1", "#EC4899",
];

// ── Helpers ──

function initials(name: string): string {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Mismo criterio que `ServiceGetListBase` con `sortBy: "nombre"`. La lista llega
 * ordenada del servidor, pero un alta o un cambio de nombre la desordenan hasta el
 * próximo sync: ordenar en el render evita depender de que cada mutación se acuerde.
 */
const collator = new Intl.Collator(undefined, { sensitivity: "base" });
const byNombre = (a: ClienteResponse, b: ClienteResponse): number =>
  collator.compare(a.nombre, b.nombre);

/**
 * El backend guarda el vencimiento como medianoche UTC. Formatearlo en la zona del
 * navegador lo corre un día hacia atrás: con Colombia en UTC−5, "2027-01-01T00:00:00Z"
 * se mostraba como "31 de dic de 2026". Se formatea en UTC para que la fecha que se ve
 * sea la que se guardó, y coincida con la que muestra el input del modal de plan. También lo usa el Dashboard.
 */
export const fmtVencimiento = (fecha: Date | string): string =>
  new Date(fecha).toLocaleDateString("es-CO", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

/**
 * Días que faltan para que venza la licencia del cliente (demo o plan), redondeados hacia arriba: 0 es el mismo día
 * y un número negativo, que ya venció. `null` si el cliente no tiene licencia.
 */
export function diasParaVencer(c: ClienteResponse): number | null {
  if (!c.fechaVencimientoPlataforma) return null;
  return Math.ceil((new Date(c.fechaVencimientoPlataforma).getTime() - Date.now()) / 86_400_000);
}

/**
 * Estado de la licencia del cliente, sea demo o plan: vigente con más de 30 días, por vencer (30 días o menos),
 * vencida o sin plan. La usan la lista de clientes y el Dashboard, para que los dos cuenten igual.
 */
export function planStatus(c: ClienteResponse): "activo" | "por-vencer" | "vencido" | "sin-plan" {
  const dias = diasParaVencer(c);
  if (dias === null) return "sin-plan";
  if (dias < 0) return "vencido";
  if (dias <= 30) return "por-vencer";
  return "activo";
}

// ── ClientesPage ──

export class ClientesPage extends BasePageComponent {
  /** Deep-link: id de cliente a auto-abrir al entrar (lo setea otra página antes de navegar aquí). */
  static openClienteId: string | null = null;

  private readonly _service = ClienteService.instance;

  /** Pestaña activa del detalle de cliente (Fincas/Plan/Usuarios/Dispositivos/Historial).
   *  Se mantiene entre clientes a propósito -si estás revisando "Usuarios" de varios
   *  clientes seguidos, no tiene sentido que cada uno te devuelva a "Fincas". */
  private _activeTab = "fincas";

  /**
   * @param _promotorId Id de promotor del usuario autenticado (`LoginPlataformaResponse.promotorId`).
   *   Si viene, la lista se acota a su cartera (`Cliente.promotorId`): acota la vista, el control
   *   de acceso lo hace el Server.
   * @param _verUsuariosApp Si se muestra la pestaña Usuarios del detalle (cuentas de la app del
   *   cliente). Sale de `calcularMenu` (`showUsuariosApp`): solo en Sextante; en Nitrofert no la ve nadie.
   *   Sin el dato no se muestra, igual que el Server, que rechaza lo que no tiene permitido.
   */
  constructor(
    private readonly _promotorId: string | null = null,
    private readonly _verUsuariosApp = false,
  ) {
    super();
  }

  private _clientes: ClienteResponse[] = [];

  // Cuentas de la app web y GPS trackers del cliente abierto en el detalle.
  // Los trae `GET /terceros/clientes/{id}` junto con el cliente y sus fincas.
  /** Ficha de finca abierta, para repintarla cuando su ortomosaico termine de publicarse. */
  private _openFinca: { f: FincaFichaResponse; c: ClienteResponse } | null = null;
  private _unwatchOrtomosaico: (() => void) | null = null;

  private _usuarios: UsuarioAppResponse[] = [];
  /** Solo lo setea `_loadUsuarios` (recargas tras una acción); la carga inicial va por el detalle. */
  private _usuariosError: "notfound" | "error" | null = null;
  private _dispositivos: ClienteDispositivoResponse[] = [];

  // DOM refs
  private _listContent!: HTMLDivElement;
  private _detailView!: HTMLDivElement;
  private _fincaDetailView!: HTMLDivElement;
  private _tbody!: HTMLTableSectionElement;
  private _totEl!: HTMLSpanElement;
  private _searchInput!: HTMLInputElement;
  private _planFilter!: HTMLSelectElement;
  private _kpiTotal!: HTMLElement;
  private _kpiActivos!: HTMLElement;
  private _kpiPorVencer!: HTMLElement;
  private _kpiVencidos!: HTMLElement;

  public getAppRoute(): string {
    return "/terceros/clientes";
  }

  public getPageTitle(): string | null {
    return "Clientes";
  }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page cl-page";
    pageContent.innerHTML = `
      <div class="cl-list-content"></div>
      <div class="cl-detail-view"></div>
      <div class="cl-finca-detail-view"></div>
    `;
    this._listContent      = pageContent.querySelector<HTMLDivElement>(".cl-list-content")!;
    this._detailView       = pageContent.querySelector<HTMLDivElement>(".cl-detail-view")!;
    this._fincaDetailView  = pageContent.querySelector<HTMLDivElement>(".cl-finca-detail-view")!;
    this._renderList();
    this._loadData();

    // El toast lo emite el servicio; acá solo repintamos la ficha si es la que está abierta.
    this._unwatchOrtomosaico = OrtomosaicoWatchService.instance.subscribe((fincaId) => {
      const abierta = this._openFinca;
      if (abierta?.f.id === fincaId) void this._goToFincaDetail(abierta.f, abierta.c);
    });
  }

  protected override beforeDispose(
    _secondSidebarWrapper: HTMLDivElement,
    _pageContent: HTMLDivElement,
  ): void {
    this._unwatchOrtomosaico?.();
    this._unwatchOrtomosaico = null;
    this._openFinca = null;
  }

  // ── List rendering ──

  private _renderList(): void {
    this._listContent.innerHTML = `
      <div class="cl-pg-hd">
        <div>
          <div class="cl-pg-ey">Módulo comercial</div>
          <div class="cl-pg-ti">Clientes</div>
          <div class="cl-pg-su">${this._promotorId ? "Tu cartera · fincas · planes · dispositivos" : "Base completa · fincas · planes · dispositivos"}</div>
        </div>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="cl-nuevo-btn">
          <i class="ph ph-plus" aria-hidden="true"></i> Nuevo cliente
        </button>
      </div>
      <div class="cl-kstrip">
        <div class="cl-kcard">
          <div class="cl-k-lbl">Total clientes</div>
          <div class="cl-k-val blue" id="cl-kpi-total">0</div>
        </div>
        <div class="cl-kcard">
          <div class="cl-k-lbl">Planes activos</div>
          <div class="cl-k-val green" id="cl-kpi-activos">0</div>
          <div class="cl-k-sub">software vigente</div>
        </div>
        <div class="cl-kcard">
          <div class="cl-k-lbl">Por vencer</div>
          <div class="cl-k-val orange" id="cl-kpi-por-vencer">0</div>
          <div class="cl-k-sub warn">próximos 30 días</div>
        </div>
        <div class="cl-kcard">
          <div class="cl-k-lbl">Vencidos</div>
          <div class="cl-k-val red" id="cl-kpi-vencidos">0</div>
          <div class="cl-k-sub">requieren renovación</div>
        </div>
      </div>
      <div class="cl-frow">
        <div class="cl-sw">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input class="cl-si" id="cl-search" autocomplete="off" placeholder="Buscar por nombre o NIT...">
        </div>
        <select class="cl-fsel" id="cl-filter-plan">
          <option value="">Todos los planes</option>
          <option value="activo">Activo</option>
          <option value="por-vencer">Por vencer</option>
          <option value="vencido">Vencido</option>
          <option value="sin-plan">Sin plan</option>
        </select>
        <span class="cl-ftot" id="cl-tot">0 clientes</span>
      </div>
      <div class="cl-tbl-wrap">
        <table class="cl-tbl">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Fincas / Área</th>
              <th>Plan</th>
              <th>Dispositivos</th>
              <th>Promotor</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="cl-tbody"></tbody>
        </table>
      </div>
    `;

    this._kpiTotal     = document.getElementById("cl-kpi-total")!;
    this._kpiActivos   = document.getElementById("cl-kpi-activos")!;
    this._kpiPorVencer = document.getElementById("cl-kpi-por-vencer")!;
    this._kpiVencidos  = document.getElementById("cl-kpi-vencidos")!;
    this._tbody        = document.getElementById("cl-tbody")! as HTMLTableSectionElement;
    this._totEl        = document.getElementById("cl-tot")!;
    this._searchInput  = document.getElementById("cl-search")! as HTMLInputElement;
    this._planFilter   = document.getElementById("cl-filter-plan")! as HTMLSelectElement;

    document.getElementById("cl-nuevo-btn")!.onclick = () => this._onCreateClick();
    this._searchInput.oninput = () => this._filterAndRender();
    this._planFilter.onchange = () => this._filterAndRender();
  }

  private _renderKpi(): void {
    let activos = 0, porVencer = 0, vencidos = 0;
    for (const c of this._clientes) {
      const s = planStatus(c);
      if (s === "activo")          activos++;
      else if (s === "por-vencer") porVencer++;
      else if (s === "vencido")    vencidos++;
    }
    this._kpiTotal.textContent     = String(this._clientes.length);
    this._kpiActivos.textContent   = String(activos);
    this._kpiPorVencer.textContent = String(porVencer);
    this._kpiVencidos.textContent  = String(vencidos);
  }

  private _renderTable(list: ClienteResponse[]): void {
    this._totEl.textContent = `${list.length} cliente${list.length === 1 ? "" : "s"}`;
    this._tbody.innerHTML = list
      .map(
        (c, i) => `
      <tr data-idx="${i}">
        <td>
          <div style="display:flex;align-items:center;gap:9px;">
            <div class="cl-cell-avatar" style="background:${avatarColor(c.nombre)}">${initials(c.nombre)}</div>
            <div>
              <div class="cl-cell-name">${this._escapeHtml(c.nombre)}</div>
              <div class="cl-cell-id">${c.nit ? this._escapeHtml(c.nit) : "NIT sin registrar"}</div>
            </div>
          </div>
        </td>
        <td>${this._renderFincasCell(c)}</td>
        <td>${this._renderPlanCell(c)}</td>
        <td>${this._renderDispositivosCell(c)}</td>
        <td style="font-size:13px;color:var(--sx-text-secondary);text-transform:uppercase">${c.promotorNombre ? this._escapeHtml(c.promotorNombre) : ""}</td>
        <td><button class="cl-chevron-btn" data-idx="${i}" aria-label="Ver detalle"><i class="ph ph-caret-right" aria-hidden="true"></i></button></td>
      </tr>`,
      )
      .join("");

    this._tbody.querySelectorAll("tr").forEach((tr) => {
      tr.onclick = (e) => {
        if ((e.target as HTMLElement).closest(".cl-chevron-btn")) return;
        const idx = Number(tr.dataset.idx);
        this._goToDetail(list[idx]);
      };
    });
    this._tbody.querySelectorAll<HTMLButtonElement>(".cl-chevron-btn").forEach((btn) => {
      btn.onclick = (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.idx);
        this._goToDetail(list[idx]);
      };
    });
  }

  private _renderFincasCell(c: ClienteResponse): string {
    if (!c.cantFincas) return '<span style="font-size:13px;color:var(--sx-text-tertiary)">—</span>';
    // 0 ha no es un área medida, es un dato que falta: mejor no mostrar nada que "0.0 ha".
    const ha = c.hectareasTotales ?? 0;
    return `
      <div style="font-size:14px;font-weight:500;color:var(--sx-text-primary)">${c.cantFincas} finca${c.cantFincas > 1 ? "s" : ""}</div>
      ${ha > 0 ? `<div class="cl-cell-ha">${ha.toFixed(1)} ha totales</div>` : ""}
    `;
  }

  /**
   * Modo + vencimiento, el mismo criterio que el tab Plan: **manda la fecha**. Sin
   * `fechaVencimientoPlataforma` no hay plan, aunque `isDemoPlataforma` sea true.
   * Es lo que hace visible el filtro de plan de la barra, que hasta ahora recortaba
   * la tabla por un estado que no se mostraba en ninguna columna.
   */
  private _renderPlanCell(c: ClienteResponse): string {
    const estado = planStatus(c);
    // Mismo guión que las celdas de fincas y dispositivos cuando no hay dato.
    if (estado === "sin-plan") return '<span style="font-size:13px;color:var(--sx-text-tertiary)">—</span>';

    const pill = c.isDemoPlataforma
      ? `<span class="cl-pill cl-pill-blue">Demo</span>`
      : `<span class="cl-pill cl-pill-ok">Producción</span>`;
    const color = estado === "vencido"
      ? "var(--sx-error-text)"
      : estado === "por-vencer"
        ? "var(--sx-warning-text)"
        : "var(--sx-text-tertiary)";
    const fecha = fmtVencimiento(c.fechaVencimientoPlataforma!);
    return `${pill}<div class="cl-cell-ha" style="color:${color};margin-top:2px">${estado === "vencido" ? "Venció" : "Vence"} ${fecha}</div>`;
  }

  private _renderDispositivosCell(c: ClienteResponse): string {
    const cant = c.cantDispositivos;
    if (!cant) return '<span style="font-size:13px;color:var(--sx-text-tertiary)">—</span>';
    // Solo el número: la columna ya dice "Dispositivos".
    return `<span class="cl-pill cl-pill-blue">${cant}</span>`;
  }

  // ── Filtering ──

  /**
   * Filtra la tabla por lo escrito (nombre o NIT, con la regla común de `busquedaTexto`: todas las palabras, sin
   * distinguir mayúsculas ni tildes) y por el plan, y la pinta ordenada por nombre con sus indicadores.
   */
  private _filterAndRender(): void {
    const palabras = palabrasBusqueda(this._searchInput.value);
    const fp = this._planFilter.value;
    const list = this._clientes.filter((c) => {
      const matchQ = coincideBusqueda(palabras, c.nombre, c.nit);
      const matchP = !fp || planStatus(c) === fp;
      return matchQ && matchP;
    }).sort(byNombre);
    this._renderKpi();
    this._renderTable(list);
  }

  // ── Data loading ──

  private async _loadData(): Promise<void> {
    try {
      // `promotorNombre` viene resuelto en el list; no hace falta cruzar con PromotorService.
      // Sí hace falta el filtro por `_promotorId`: `ClienteService` es una lista global sin
      // scope server-side, así que la vista acotada de un promotor se filtra client-side.
      const clientes = await this._service.get();
      this._clientes = this._promotorId
        ? clientes.filter((c) => c.promotorId === this._promotorId)
        : clientes;
      this._filterAndRender();
      this._openPendingCliente();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar clientes", (error as Error).message);
    }
  }

  /** Deep-link: si otra página pidió abrir un cliente concreto, lo abre y limpia la señal. */
  private _openPendingCliente(): void {
    const id = ClientesPage.openClienteId;
    if (!id) return;
    ClientesPage.openClienteId = null;
    const cliente = this._clientes.find((c) => c.id === id);
    if (cliente) void this._goToDetail(cliente);
  }

  // ── Navigation ──

  /**
   * Todo el detalle sale de `GET /terceros/clientes/{id}`: cliente, fincas (con
   * `cultivos`, sin lotes), cuentas de la app y dispositivos. Los lotes de una finca
   * se cargan aparte, solo al abrir esa finca (ver `_goToFincaDetail`).
   */
  private async _goToDetail(cliente: ClienteResponse): Promise<void> {
    this._fincaDetailView.classList.remove("active");
    this._listContent.style.display = "none";
    // Antes de esperar la red: si no se limpia acá, durante la espera se ve el detalle
    // del cliente anterior todavía pintado -_goToList() solo lo oculta, no lo vacía-.
    this._detailView.innerHTML = "";
    this._detailView.classList.add("active");

    let detalle: ClienteDetalleResponse;
    try {
      detalle = await this._service.getDetalle(cliente.id);
    } catch (error) {
      // 404 = el cliente no existe o lo eliminaron desde otra sesión: no es un fallo de red.
      if (error instanceof NotFoundError) {
        this._renderClienteNotFound();
        return;
      }
      errorMessageComponent.addError("Error al cargar el cliente", (error as Error).message);
      this._goToList();
      return;
    }

    this._usuariosError = null;
    this._usuarios      = detalle.usuariosApp; // ya vienen con los hasUsuarios primero
    this._dispositivos  = [...detalle.dispositivos].sort((a, b) => b.codigoInterno - a.codigoInterno);

    // El detalle trae el cliente fresco: aprovecho para refrescar la fila del listado.
    this._clientes = this._clientes.map((c) => (c.id === detalle.cliente.id ? detalle.cliente : c));

    this._renderDetail({ ...detalle.cliente, fincas: detalle.fincas });
  }

  private _renderClienteNotFound(): void {
    this._detailView.innerHTML = `
      <div class="cl-det-topbar">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="cl-det-back">
          <i class="ph ph-arrow-left" aria-hidden="true"></i> Clientes
        </button>
      </div>
      ${this._emptyState(
        "ph ph-warning-circle",
        "Cliente no encontrado",
        "El servidor no reconoce este cliente. Puede haberse eliminado desde otra sesión.",
      )}
    `;
    document.getElementById("cl-det-back")!.onclick = () => this._goToList();
  }

  private _goToList(): void {
    this._openFinca = null;
    this._detailView.classList.remove("active");
    this._fincaDetailView.classList.remove("active");
    this._listContent.style.display = "";
    // La tabla es HTML estático: sin esto sigue mostrando lo que se pintó al cargar.
    // Cubre todo lo que se puede tocar desde el detalle — plan, datos del cliente,
    // fincas — y de paso refresca los KPI, que sufren la misma staleness.
    this._filterAndRender();
  }

  /**
   * `GET /terceros/fincas/{id}?clienteId=` trae la finca y sus lotes juntos. Se pide
   * siempre, incluso al volver de editar: además de los lotes, es la única vía por la
   * que llegan `conteoMultiPoint`, `zipOrtomosaico` y los datos del dueño, que no
   * vienen en el detalle del cliente ni en la respuesta del save.
   */
  private async _goToFincaDetail(f: Pick<FincaFichaResponse, "id">, c: ClienteResponse): Promise<void> {
    this._detailView.classList.remove("active");
    // Mismo criterio que _goToDetail: vaciar antes de esperar la red, para no dejar
    // ver la finca anterior mientras carga la nueva.
    this._fincaDetailView.innerHTML = "";
    this._fincaDetailView.classList.add("active");

    let finca: FincaFichaResponse;
    try {
      finca = await FincaGetByIdService.instance.getById(f.id, c.id);
    } catch (error) {
      errorMessageComponent.addError(
        error instanceof NotFoundError ? "Finca no encontrada" : "Error al cargar la finca",
        error instanceof NotFoundError
          ? "El servidor no reconoce esta finca. Puede haberse eliminado desde otra sesión."
          : (error as Error).message,
      );
      this._backToClientDetail();
      return;
    }

    this._renderFincaDetailView(finca, c);
  }

  private _backToClientDetail(): void {
    this._openFinca = null;
    this._fincaDetailView.classList.remove("active");
    this._detailView.classList.add("active");
  }

  // ── Client detail rendering ──

  /** " on" si `tab` es la pestaña activa recordada en `_activeTab`, "" si no. */
  private _tabOn(tab: string): string {
    return this._activeTab === tab ? " on" : "";
  }

  /**
   * Detalle del cliente: barra superior, encabezado con sus indicadores y las pestañas Fincas, Plan,
   * Usuarios, Dispositivos e Historial comercial. Usuarios solo si el rol la ve (`_verUsuariosApp`).
   */
  private _renderDetail(c: ClienteResponse): void {
    const totalArea  = c.hectareasTotales > 0 ? `${c.hectareasTotales.toFixed(1)} ha` : "—";
    const cantDisp   = c.cantDispositivos;

    // Pestaña Usuarios (cuentas de la app): solo en Sextante.
    const tabUsuarios = this._verUsuariosApp
      ? `<div class="cl-tab${this._tabOn("usuarios")}" data-tab="usuarios">Usuarios ${this._usuarios.length > 0 ? `<span class="cl-tab-c">${this._usuarios.length}</span>` : ""}</div>`
      : "";
    const panelUsuarios = this._verUsuariosApp
      ? `<div class="cl-tp${this._tabOn("usuarios")}" id="tp-usuarios"></div>`
      : "";

    this._detailView.innerHTML = `
      <div class="cl-det-topbar">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="cl-det-back">
          <i class="ph ph-arrow-left" aria-hidden="true"></i> Clientes
        </button>
        <div class="cl-det-sep"></div>
        <span class="cl-det-breadcrumb">${this._escapeHtml(c.nombre)}</span>
        <div class="cl-det-sp"></div>
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="cl-det-edit">
          <i class="ph ph-pencil" aria-hidden="true"></i> Editar
        </button>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="cl-det-new-finca">
          <i class="ph ph-plus" aria-hidden="true"></i> Nueva finca
        </button>
      </div>
      <div class="cl-det-hd-wrap">
        <div class="cl-det-hd-top">
          <div class="cl-det-avatar" style="background:${avatarColor(c.nombre)}">${initials(c.nombre)}</div>
          <div class="cl-det-hd-info">
            <div class="cl-det-hd-name">${this._escapeHtml(c.nombre)}</div>
            <div class="cl-det-hd-id">${c.nit ? `NIT ${this._escapeHtml(c.nit)}` : "NIT sin registrar"}</div>
            <div class="cl-det-hd-meta">
              ${c.telefono ? `<span style="font-size:12px;color:var(--sx-text-tertiary)"><i class="ph ph-phone" aria-hidden="true"></i> ${this._escapeHtml(c.telefono)}</span>` : ""}
              ${c.email ? `<span style="font-size:12px;color:var(--sx-text-tertiary)"><i class="ph ph-envelope" aria-hidden="true"></i> ${this._escapeHtml(c.email)}</span>` : ""}
              ${c.promotorNombre ? `<span style="font-size:12px;color:var(--sx-text-tertiary)">Promotor: ${this._escapeHtml(c.promotorNombre)}</span>` : ""}
            </div>
          </div>
          <div class="cl-det-hd-actions">
            ${c.telefono ? `<button class="sx-btn sx-btn-ghost sx-btn-sm" id="cl-det-call"><i class="ph ph-phone" aria-hidden="true"></i> Llamar</button>` : ""}
          </div>
        </div>
        <div class="cl-hd-chips">
          <div class="cl-hd-chip"><div class="cl-hd-chip-val blue">${c.cantFincas}</div><div class="cl-hd-chip-lbl">Fincas</div></div>
          <div class="cl-hd-chip"><div class="cl-hd-chip-val">${totalArea}</div><div class="cl-hd-chip-lbl">Área total</div></div>
          <div class="cl-hd-chip"><div class="cl-hd-chip-val">${cantDisp}</div><div class="cl-hd-chip-lbl">Dispositivos</div></div>
        </div>
      </div>
      <div class="cl-tabs">
        <div class="cl-tab${this._tabOn("fincas")}" data-tab="fincas">Fincas ${c.cantFincas > 0 ? `<span class="cl-tab-c">${c.cantFincas}</span>` : ""}</div>
        <div class="cl-tab${this._tabOn("plan")}" data-tab="plan">Plan</div>
        ${tabUsuarios}
        <div class="cl-tab${this._tabOn("dispositivos")}" data-tab="dispositivos">Dispositivos ${cantDisp > 0 ? `<span class="cl-tab-c">${cantDisp}</span>` : ""}</div>
        <div class="cl-tab${this._tabOn("historial")}" data-tab="historial">Historial comercial</div>
      </div>
      <div class="cl-tp${this._tabOn("fincas")}" id="tp-fincas"></div>
      <div class="cl-tp${this._tabOn("plan")}" id="tp-plan"></div>
      ${panelUsuarios}
      <div class="cl-tp${this._tabOn("dispositivos")}" id="tp-dispositivos"></div>
      <div class="cl-tp${this._tabOn("historial")}" id="tp-historial"></div>
    `;

    document.getElementById("cl-det-back")!.onclick      = () => this._goToList();
    document.getElementById("cl-det-edit")!.onclick      = () => this._onEditClick(c);
    document.getElementById("cl-det-new-finca")!.onclick = () => this._onNewFincaClick(c);
    document.getElementById("cl-det-call")?.addEventListener("click", () => {
      if (c.telefono) window.location.href = `tel:${c.telefono}`;
    });

    this._detailView.querySelectorAll<HTMLElement>(".cl-tab").forEach((tab) => {
      tab.onclick = () => {
        this._detailView.querySelectorAll(".cl-tab").forEach((t) => t.classList.remove("on"));
        this._detailView.querySelectorAll(".cl-tp").forEach((t) => t.classList.remove("on"));
        tab.classList.add("on");
        this._activeTab = tab.dataset.tab!;
        const tp = document.getElementById(`tp-${tab.dataset.tab}`);
        if (tp) tp.classList.add("on");
      };
    });

    this._renderFincasTab(c);
    this._renderPlanTab(c);
    if (this._verUsuariosApp) this._renderUsuariosTab(c);
    this._renderDispositivosTab();
  }

  // ── Pestaña Usuarios: cuentas de la app web (DATA AG) del cliente ──

  /**
   * Re-sincroniza las cuentas tras una acción (activar/inactivar/restablecer/editar),
   * que responden 204 sin el registro. La carga inicial no pasa por acá: viene en
   * `usuariosApp` del detalle del cliente.
   */
  private async _loadUsuarios(clienteId: string): Promise<void> {
    this._usuariosError = null;
    try {
      await UsuarioAppListByClienteIdService.instance(clienteId).sync();
    } catch (error) {
      // 404 = el cliente no existe en el servidor. Cualquier otro error deja ver la última lista cargada.
      this._usuariosError = error instanceof NotFoundError ? "notfound" : "error";
    }
    this._usuarios = UsuarioAppListByClienteIdService.instance(clienteId).items
      .sort((a, b) => Number(b.hasUsuarios) - Number(a.hasUsuarios) || a.nombre.localeCompare(b.nombre));
  }

  /**
   * Pestaña Usuarios: cuentas de la app web del cliente con sus acciones. "Nuevo usuario" queda
   * deshabilitado (con aviso) si el cliente no tiene licencia asignada.
   */
  private _renderUsuariosTab(c: ClienteResponse): void {
    const container = document.getElementById("tp-usuarios");
    if (!container) return;

    // "Cliente inexistente" y "cliente sin cuentas" son casos distintos: no colapsarlos.
    if (this._usuariosError === "notfound") {
      container.innerHTML = this._emptyState(
        "ph ph-warning-circle",
        "Cliente no encontrado",
        "El servidor no reconoce este cliente. Puede haberse eliminado desde otra sesión.",
      );
      return;
    }

    const aviso = this._usuariosError === "error"
      ? `<div class="cl-pill cl-pill-wa" style="margin-bottom:10px">No se pudo actualizar la lista: mostrando la última carga.</div>`
      : "";

    // Cliente sin licencia (nunca se le asignó plan): no se le pueden crear cuentas. El servidor lo
    // rechaza igual; acá se evita llegar al formulario.
    const sinLicencia = !c.fechaVencimientoPlataforma;
    const avisoLicencia = sinLicencia
      ? `<div class="cl-pill cl-pill-wa" style="margin-bottom:10px">Asigne un plan al cliente (pestaña Plan) para poder crear usuarios.</div>`
      : "";

    const body = this._usuarios.length === 0
      ? this._emptyState(
          "ph ph-users",
          "Sin usuarios",
          "Este cliente todavía no tiene cuentas de acceso a la aplicación web.",
        )
      : `<div class="cl-disp-list">${this._usuarios.map((u) => this._usuarioRowHtml(u)).join("")}</div>`;

    container.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px">
        <div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">
          Cuentas con acceso a la aplicación web (DATA AG).
        </div>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="cl-usr-new"${sinLicencia ? ` disabled title="El cliente no tiene una licencia asignada"` : ""}>
          <i class="ph ph-plus" aria-hidden="true"></i> Nuevo usuario
        </button>
      </div>
      ${avisoLicencia}
      ${aviso}
      ${body}
    `;

    container.querySelector<HTMLButtonElement>("#cl-usr-new")!.onclick = () => {
      new UsuarioAppFormModal(c.id, () => void this._reloadUsuarios(c)).init();
    };

    container.querySelectorAll<HTMLButtonElement>("[data-uid]").forEach((btn) => {
      btn.onclick = () => {
        const usuario = this._usuarios.find((u) => u.id === btn.dataset.uid);
        if (usuario) this._onUsuarioAction(c, usuario, btn.dataset.act ?? "");
      };
    });
  }

  private _usuarioRowHtml(u: UsuarioAppResponse): string {
    const estado = u.isActivo
      ? `<span class="cl-pill cl-pill-ok">Activa</span>`
      : `<span class="cl-pill cl-pill-er" title="No puede iniciar sesión ni recuperar su contraseña">Suspendida</span>`;

    const toggle = u.isActivo
      ? { act: "inactivar", icon: "ph-prohibit", title: "Suspender cuenta" }
      : { act: "activar", icon: "ph-check-circle", title: "Reactivar cuenta" };

    return `
      <div class="cl-disp-item">
        <div class="cl-disp-icon"><i class="ph ph-user" aria-hidden="true"></i></div>
        <div class="cl-disp-info">
          <div class="cl-disp-name">${this._escapeHtml(u.nombre)} ${estado}</div>
          <div class="cl-disp-meta">${this._escapeHtml(u.email)}</div>
        </div>
        <div class="cl-disp-status" style="display:flex;align-items:center;gap:6px">
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-uid="${u.id}" data-act="editar" title="Editar">
            <i class="ph ph-pencil" aria-hidden="true"></i>
          </button>
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-uid="${u.id}" data-act="${toggle.act}" title="${toggle.title}">
            <i class="ph ${toggle.icon}" aria-hidden="true"></i>
          </button>
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-uid="${u.id}" data-act="reset" title="Restablecer contraseña">
            <i class="ph ph-key" aria-hidden="true"></i>
          </button>
        </div>
      </div>`;
  }

  private _onUsuarioAction(c: ClienteResponse, u: UsuarioAppResponse, act: string): void {
    if (act === "editar") {
      new UsuarioAppFormModal(c.id, () => void this._reloadUsuarios(c), u).init();
      return;
    }
    if (act === "activar") {
      // Reactivar no expulsa a nadie: no necesita confirmación.
      void this._runUsuarioAccion(c, () => UsuarioAppListByClienteIdService.instance(c.id).activar(u.id),
        "Cuenta reactivada", `${u.nombre} ya puede iniciar sesión.`);
      return;
    }
    if (act === "inactivar") {
      new ConfirmDialogComponent({
        title: "Suspender cuenta",
        message: `${this._escapeHtml(u.nombre)} (${this._escapeHtml(u.email)}) quedará afuera al instante: se cierran sus sesiones abiertas y no podrá iniciar sesión ni recuperar su contraseña hasta que la reactives.`,
        confirmLabel: "Suspender",
        onConfirm: () => void this._runUsuarioAccion(c, () => UsuarioAppListByClienteIdService.instance(c.id).inactivar(u.id),
          "Cuenta suspendida", `${u.nombre} ya no puede acceder.`),
      }).init();
      return;
    }
    if (act === "reset") {
      new ConfirmDialogComponent({
        title: "Restablecer contraseña",
        message: `Se generará una contraseña nueva y se enviará a ${this._escapeHtml(u.email)}. Las sesiones abiertas de esta cuenta se cerrarán de inmediato.`,
        confirmLabel: "Restablecer",
        onConfirm: () => void this._runUsuarioAccion(c, () => UsuarioAppListByClienteIdService.instance(c.id).restablecerPassword(u.id),
          "Contraseña restablecida", `Se envió la contraseña nueva a ${u.email}.`),
      }).init();
    }
  }

  private async _runUsuarioAccion(
    c: ClienteResponse,
    accion: () => Promise<void>,
    okTitle: string,
    okDesc: string,
  ): Promise<void> {
    try {
      await accion();
      errorMessageComponent.addSuccess(okTitle, okDesc);
      await this._reloadUsuarios(c);
    } catch (error) {
      errorMessageComponent.addError(
        "No se pudo completar la acción",
        error instanceof Error ? error.message : "Error desconocido.",
      );
    }
  }

  private async _reloadUsuarios(c: ClienteResponse): Promise<void> {
    await this._loadUsuarios(c.id);
    this._renderUsuariosTab(c);
    const tab = this._detailView.querySelector<HTMLElement>('.cl-tab[data-tab="usuarios"]');
    if (tab) {
      tab.innerHTML = `Usuarios ${this._usuarios.length > 0 ? `<span class="cl-tab-c">${this._usuarios.length}</span>` : ""}`;
    }
  }

  /**
   * Pestaña Dispositivos: GPS trackers del cliente, ya filtrados por el detalle (antes se recargaba toda la tabla de
   * trackers para quedarse con los de este cliente). Cada uno tiene el botón para descargar el CSV de un día
   * (`GpsTrackerDescargaModal`, 2026-10-29).
   */
  private _renderDispositivosTab(): void {
    const container = document.getElementById("tp-dispositivos");
    if (!container) return;

    const devices = this._dispositivos;

    if (devices.length === 0) {
      container.innerHTML = this._emptyState("ph-cpu", "Sin dispositivos asignados", "Este cliente no tiene dispositivos de hardware asignados.");
      return;
    }

    container.innerHTML = `
      <div class="cl-disp-list">
        ${devices.map((d) => {
          const sinTest = !d.testeado;
          const testPill = sinTest
            ? `<span class="cl-pill cl-pill-na">Sin testear</span>`
            : d.pasaTest
              ? `<span class="cl-pill cl-pill-ok">Pasa test</span>`
              : `<span class="cl-pill cl-pill-er" title="${this._escapeHtml(d.notaTest ?? "")}">No pasa test</span>`;
          return `
            <div class="cl-disp-item">
              <div class="cl-disp-icon"><i class="ph ph-cpu" aria-hidden="true"></i></div>
              <div class="cl-disp-info">
                <div class="cl-disp-name">GPS Tracker · ${d.codigoInterno}</div>
                <div class="cl-disp-meta">N° cliente: ${d.codigoCliente}</div>
              </div>
              <div class="cl-disp-status">${testPill}</div>
              <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" type="button" data-descargar-gps="${d.codigoInterno}" title="Descargar CSV de un día" aria-label="Descargar CSV de un día"><i class="ph ph-download-simple" aria-hidden="true"></i></button>
            </div>`;
        }).join("")}
      </div>
    `;
    container.querySelectorAll<HTMLButtonElement>("[data-descargar-gps]").forEach((boton) => {
      boton.onclick = () => new GpsTrackerDescargaModal(Number(boton.dataset.descargarGps)).init();
    });
  }

  // Pestaña Fincas: cards simples clicables, cada una abre el detalle de finca
  private _renderFincasTab(c: ClienteResponse): void {
    const container = document.getElementById("tp-fincas")!;
    const fincas = c.fincas;
    if (!fincas || fincas.length === 0) {
      container.innerHTML = this._emptyState("ph-buildings", "Sin fincas registradas", "Este cliente no tiene fincas asociadas.");
      return;
    }

    container.innerHTML = `
      <div class="cl-finca-list">
        ${fincas.map((f) => {
          const meta = this._fincaMeta(f);
          return `
            <div class="cl-finca-item" data-fid="${f.id}">
              <div class="cl-fi-icon"><i class="ph ph-house-line" aria-hidden="true"></i></div>
              <div class="cl-fi-info">
                <div class="cl-fi-name">${this._escapeHtml(f.nombreFinca)}</div>
                ${meta ? `<div class="cl-fi-meta">${this._escapeHtml(meta)}</div>` : ""}
              </div>
              <div class="cl-fi-stats">
                <div class="cl-fi-stat-val">${f.hectareasFinca} ha</div>
                <div class="cl-fi-stat-lbl">área total</div>
              </div>
              <i class="ph ph-caret-right cl-fi-chevron" aria-hidden="true"></i>
            </div>
          `;
        }).join("")}
      </div>
    `;

    container.querySelectorAll<HTMLElement>(".cl-finca-item").forEach((item) => {
      item.onclick = () => {
        const finca = fincas.find((f) => f.id === item.dataset.fid);
        if (finca) void this._goToFincaDetail(finca, c);
      };
    });
  }

  // "Municipio, Departamento · Cultivo principal" para la card de lista de fincas
  private _fincaMeta(f: FincaResumenResponse): string {
    const location = [f.municipioNombre, f.departamentoNombre].filter(Boolean).join(", ");
    return [location, this._cultivoPpal(f)].filter(Boolean).join(" · ");
  }

  /**
   * Cultivo principal de la finca. Sale de `f.cultivos` (nombres distintos de los
   * lotes, resueltos por el servidor): así no hay que cargar los lotes solo para
   * poner la etiqueta. Cae a los lotes si ya están en memoria y el campo no vino.
   */
  private _cultivoPpal(f: Omit<FincaResumenResponse, "isEliminado"> & { lotes?: FincaFichaResponse["lotes"] }): string {
    const cultivos = f.cultivos
      ?? [...new Set((f.lotes ?? []).map((l) => l.cultivo?.nombre).filter((n): n is string => !!n))];
    if (cultivos.length === 0) return "";
    if (cultivos.length === 1) return cultivos[0];
    return "Múltiple";
  }

  // ── Plan tab ──

  /**
   * Pestaña Plan: modo (Demo, Producción o Sin plan), vencimiento y tope de hectáreas, con el botón
   * que abre `AsignarPlanModal`. Al guardar repinta el detalle y, si se ve la pestaña Usuarios,
   * recarga las cuentas.
   */
  private _renderPlanTab(c: ClienteResponse): void {
    const container = document.getElementById("tp-plan")!;

    const diasRestantes = c.fechaVencimientoPlataforma
      ? Math.ceil((new Date(c.fechaVencimientoPlataforma).getTime() - Date.now()) / 86_400_000)
      : null;

    const vencimientoStr = c.fechaVencimientoPlataforma
      ? fmtVencimiento(c.fechaVencimientoPlataforma)
      : "Sin asignar";

    const expLabel = diasRestantes === null
      ? "Sin fecha de vencimiento"
      : diasRestantes < 0
        ? `Vencido hace ${Math.abs(diasRestantes)} día${Math.abs(diasRestantes) === 1 ? "" : "s"}`
        : diasRestantes === 0
          ? "Vence hoy"
          : `Vence en ${diasRestantes} día${diasRestantes === 1 ? "" : "s"}`;

    const expColor = diasRestantes === null || diasRestantes > 30
      ? "var(--sx-text-tertiary)"
      : diasRestantes < 0
        ? "var(--sx-error-text)"
        : "var(--sx-warning-text)";

    // Modo del plan: sin fecha de vencimiento no hay plan, sea demo o no.
    // Con fecha, el tipo lo decide isDemoPlataforma.
    const tienePlan = !!c.fechaVencimientoPlataforma;
    const modo: "Demo" | "Producción" | "Sin plan" =
      !tienePlan ? "Sin plan" : c.isDemoPlataforma ? "Demo" : "Producción";

    // El tope de hectáreas solo rige en Producción: en Demo y Sin plan no aplica,
    // y en Producción con 0 el tope quedó sin definir.
    const maxHaStr =
      modo !== "Producción"
        ? "No aplica"
        : c.maximoHectareasPlataforma > 0
          ? `${c.maximoHectareasPlataforma} ha`
          : "Sin establecer";

    const demoBadge =
      modo === "Demo"
        ? `<span class="cl-pill cl-pill-blue">Demo</span>`
        : modo === "Producción"
          ? `<span class="cl-pill cl-pill-ok">Producción</span>`
          : `<span class="cl-pill cl-pill-na">Sin plan</span>`;

    container.innerHTML = `
      <div class="cl-plan-card">
        <div class="cl-plan-hd">
          <div>
            <div class="cl-plan-name">Plataforma Sextante</div>
            <div class="cl-plan-sub">Acceso y límites de uso</div>
          </div>
          ${demoBadge}
        </div>
        <div class="cl-plan-row">
          <span class="cl-plan-lbl">Modo</span>
          <span class="cl-plan-val">${modo}</span>
        </div>
        <div class="cl-plan-row">
          <span class="cl-plan-lbl">Vencimiento</span>
          <span class="cl-plan-val">${vencimientoStr}</span>
        </div>
        <div class="cl-plan-row">
          <span class="cl-plan-lbl">Máx. hectáreas en plataforma</span>
          <span class="cl-plan-val">${maxHaStr}</span>
        </div>
        ${tienePlan ? `<div class="cl-exp-label" style="color:${expColor}">${expLabel}</div>` : ""}
        <div class="cl-plan-actions">
          <button class="sx-btn sx-btn-secondary sx-btn-sm" id="cl-plan-edit-btn">
            <i class="ph ${modo === "Sin plan" ? "ph-plus" : "ph-pencil"}" aria-hidden="true"></i>
            ${modo === "Sin plan" ? "Nuevo plan" : "Actualizar plan"}
          </button>
        </div>
      </div>
    `;

    container.querySelector("#cl-plan-edit-btn")!.addEventListener("click", () => {
      new AsignarPlanModal(c, (saved) => {
        this._clientes = this._clientes.map((x) => (x.id === saved.id ? saved : x));
        // Repinta la tabla ya, sin esperar al `_goToList()`: si se vuelve por el menú
        // lateral o con el botón atrás del navegador, ese nunca corre.
        this._filterAndRender();
        this._renderDetail({ ...saved, fincas: c.fincas });
        // Asignar el primer plan puede crear la cuenta de acceso del cliente en el
        // servidor (ver CLAUDE.md) — refrescar la pestaña Usuarios, mismo criterio
        // que ya usa el resto de las acciones de esta página. Sin la pestaña no se
        // pide: el Server rechazaría la consulta.
        if (this._verUsuariosApp) void this._reloadUsuarios(saved);
      }).init();
    });
  }

  // ── Finca detail rendering ──

  private _renderFincaDetailView(f: FincaFichaResponse, c: ClienteResponse): void {
    this._openFinca     = { f, c };
    const lotes         = f.lotes ?? [];
    const isActiva      = f.plataformaActiva ?? false;

    const locationParts = [f.municipioNombre, f.departamentoNombre].filter(Boolean).join(", ");
    const regionPart    = f.departamentoRegionNombre ? `Región ${f.departamentoRegionNombre}` : "";
    const subtitle      = [c.nombre, locationParts, regionPart].filter(Boolean).join(" · ");

    const platBadge = isActiva
      ? `<span class="cl-fd-badge cl-fd-badge-ok"><span class="cl-fd-badge-dot"></span>Plataforma activa</span>`
      : `<span class="cl-fd-badge cl-fd-badge-na"><span class="cl-fd-badge-dot"></span>Sin plataforma</span>`;

    const tipoCultivo = [f.tipo, this._cultivoPpal(f)].filter(Boolean).join(" · ");
    const tipoBadge   = tipoCultivo
      ? `<span class="cl-fd-badge cl-fd-badge-blue">${this._escapeHtml(tipoCultivo)}</span>`
      : "";

    this._fincaDetailView.innerHTML = `
      <div class="cl-det-topbar">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="fd-back-list">
          <i class="ph ph-arrow-left" aria-hidden="true"></i> Clientes
        </button>
        <div class="cl-det-sep"></div>
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="fd-back-client">${this._escapeHtml(c.nombre)}</button>
        <div class="cl-det-sep"></div>
        <span class="cl-det-breadcrumb">${this._escapeHtml(f.nombreFinca)}</span>
        <div class="cl-det-sp"></div>
        ${isActiva ? `<span class="cl-fd-plat-chip">DATA AG</span>` : ""}
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="fd-edit-finca">
          <i class="ph ph-pencil" aria-hidden="true"></i> Editar
        </button>
      </div>
      <div class="cl-fd-hd-wrap">
        <div class="cl-fd-hd-top">
          <div class="cl-fd-hd-icon"><i class="ph ph-house-line" aria-hidden="true"></i></div>
          <div class="cl-fd-hd-info">
            <div class="cl-fd-hd-name">${this._escapeHtml(f.nombreFinca)}</div>
            <div class="cl-fd-hd-sub">${this._escapeHtml(subtitle)}</div>
            <div class="cl-fd-badges">
              ${platBadge}
              ${tipoBadge}
            </div>
          </div>
          <button class="sx-btn sx-btn-danger sx-btn-sm" id="fd-del-finca">
            <i class="ph ph-trash" aria-hidden="true"></i> Eliminar
          </button>
        </div>
        <div class="cl-fd-kpi-bar">
          <div class="cl-fd-kpi-item">
            <span class="cl-fd-kpi-val">${f.hectareasFinca} ha</span>
            <span class="cl-fd-kpi-lbl">Área total</span>
          </div>
          <div class="cl-fd-kpi-item">
            <span class="cl-fd-kpi-val">${lotes.length}</span>
            <span class="cl-fd-kpi-lbl">Lotes</span>
          </div>
        </div>
      </div>
      <div class="cl-tp on" id="fd-tp-general"></div>
    `;

    document.getElementById("fd-back-list")!.onclick   = () => this._goToList();
    document.getElementById("fd-back-client")!.onclick = () => this._backToClientDetail();
    document.getElementById("fd-edit-finca")!.onclick  = () => this._onEditFincaClick(f, c);
    document.getElementById("fd-del-finca")!.onclick   = () => this._onDeleteFincaClick(f, c);

    this._renderFincaDetailGeneral(f, c);
  }

  private _renderFincaDetailGeneral(f: FincaFichaResponse, c: ClienteResponse): void {
    const container   = document.getElementById("fd-tp-general")!;
    const lotes       = f.lotes ?? [];
    const totalHa     = lotes.reduce((s, l) => s + l.areaHectareas, 0).toFixed(2);
    const cultivoPpal = this._cultivoPpal(f) || "—";
    const isActiva    = f.plataformaActiva ?? false;

    const datosRows: [string, string][] = [
      ["Cliente",           c.nombre],
      ["Hectáreas",         `${f.hectareasFinca} ha`],
      ["Departamento",      f.departamentoNombre       ?? "—"],
      ["Municipio",         f.municipioNombre          ?? "—"],
      ["Región",            f.departamentoRegionNombre ?? "—"],
      ["Tipo",              f.tipo                     ?? "—"],
      ["Cultivo principal", cultivoPpal],
    ];

    const platDot   = `<span class="cl-fp-plat-dot ${isActiva ? "cl-fp-plat-dot-ok" : "cl-fp-plat-dot-na"}"></span>`;
    const platLabel = isActiva ? "Activada" : "Sin plataforma";
    const platSub   = isActiva ? "Visible en front · PostGIS activo" : "Sin suscripción activa";

    const lotesBody = lotes.map((l) => `
      <tr>
        <td>${this._escapeHtml(l.nombreLote)}</td>
        <td>${this._escapeHtml(l.cultivo?.nombre ?? "—")}</td>
        <td>${l.areaHectareas}</td>
        <td>${this._renderTipoMonitoreo(l.tipoMonitoreo ?? null)}</td>
      </tr>`).join("");

    container.innerHTML = `
      <div class="cl-fd-general-wrap">
        <div class="cl-fp-grid" id="fd-grid-${f.id}">
          <div class="cl-fp-card">
            <div class="cl-fp-card-hd">Datos de la finca</div>
            ${datosRows.map(([lbl, val]) => `
              <div class="cl-fp-row">
                <span class="cl-fp-lbl">${lbl}</span>
                <span class="cl-fp-val">${this._escapeHtml(val)}</span>
              </div>`).join("")}
          </div>
          <div class="cl-fp-card">
            <div class="cl-fp-card-hd">Plataforma</div>
            <div class="cl-fp-plat-status">
              ${platDot}
              <div>
                <div class="cl-fp-plat-label">${platLabel}</div>
                <div class="cl-fp-plat-sub">${platSub}</div>
              </div>
            </div>
          </div>
        </div>
        ${this._renderGisSection(f)}
        <div class="cl-fp-lotes-card">
          <div class="cl-fp-lotes-hd">
            <div>
              <div class="cl-fp-lotes-title">Lotes</div>
              <div class="cl-fp-lotes-meta">${lotes.length} lote${lotes.length === 1 ? "" : "s"} · ${totalHa} ha totales</div>
            </div>
          </div>
          ${lotes.length > 0 ? `
            <table class="cl-lotes-tbl">
              <thead><tr>
                <th>Lote</th><th>Cultivo</th><th>Área (ha)</th><th>Tipo monitoreo</th>
              </tr></thead>
              <tbody>${lotesBody}</tbody>
            </table>
          ` : `<div style="padding:20px 16px;font-size:var(--sx-text-sm);color:var(--sx-text-tertiary)">Sin lotes registrados.</div>`}
        </div>
      </div>
    `;
    this._wireGisUploads(f, c);
  }

  // Archivos GIS. Los KML se GENERAN acá desde el GeoJSON que ya tenemos
  // (`shared/utils/kmlUtils.ts`); no hace falta librería porque KML y GeoJSON
  // comparten el orden lon,lat[,alt]. El ZIP de ortomosaico, en cambio, se SUBE
  // (GET storage/sas-url → PUT a Azure Blob → GuardarOrtomosaicoService.guardar).
  //   - AOI    → `f.sateliteAreaGeografica`, que sí viaja en el detalle del cliente.
  //   - Lotes  → `f.lotes[].areaGeograficaPolygon`. NO se usa `lotesFeatureCollection`:
  //     ese campo solo existe en `ClienteFincaResponse` y tanto el detalle del cliente
  //     como el list devuelven la versión recortada `ClienteFincaBaseResponse`.
  //   - Conteo → `f.conteoMultiPoint`, un punto por árbol contado.
  //
  // Los tres salen del detalle de finca (`GET /terceros/fincas/{id}`); el conteo solo
  // viaja ahí. Ojo con el conteo: hoy únicamente lo tienen las fincas migradas del
  // sistema anterior — lo escribe el initializer del backend y no hay endpoint para
  // cargarlo, así que en las fincas nuevas la fila queda deshabilitada y no es un bug.
  private _renderGisSection(f: FincaFichaResponse): string {
    const row = (icon: string, iconCls: string, name: string, status: string, action: string): string => `
      <div class="cl-gis-file">
        <div class="cl-gis-file-ico ${iconCls}"><i class="${icon}" aria-hidden="true"></i></div>
        <div class="cl-gis-file-info">
          <div class="cl-gis-file-name">${name}</div>
          <div class="cl-gis-file-status">${status}</div>
        </div>
        ${action}
      </div>
    `;
    // AOI: `sateliteAreaGeografica` viene en el list de fincas. La interfaz lo
    // declara no-nulo pero el API puede no traerlo, de ahí el guard.
    // Acepta .kml plano o .kmz/.zip (un solo polígono) — mismo flujo de subida
    // que conteo/ortomosaico: GET storage/sas-url → PUT → POST guardar-aoi.
    const aoi = f.sateliteAreaGeografica as Polygon | null | undefined;
    const aoiVertices = aoi?.coordinates?.length ? countPolygonVertices(aoi) : 0;
    const aoiRow = row(
      "ph ph-navigation-arrow",
      "cl-gis-ico-kml",
      "KML Área de interés (AOI)",
      aoiVertices > 0 ? `Polígono · ${aoiVertices} vértices` : "Sin geometría",
      `<input type="file" accept=".kml,.kmz,.zip" style="display:none" id="cl-gis-aoi-input-${f.id}">
       ${aoiVertices > 0 ? `
         <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-aoi-btn-${f.id}">
           <i class="ph ph-download-simple" aria-hidden="true"></i> Descargar
         </button>` : ""}
       <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-aoi-up-${f.id}">
         <i class="ph ph-upload-simple" aria-hidden="true"></i> Cargar
       </button>`,
    );
    // Lotes: los eliminados ya vienen filtrados por ServiceGetListBase, y el API
    // declara `areaGeograficaPolygon` required y no-nullable — el guard es solo por
    // si se rompe ese contrato, no un caso esperado.
    // Requiere que la finca ya tenga AOI (400 FINCA_SIN_AOI) — el botón de carga
    // queda deshabilitado hasta entonces, mismo orden que documenta el servidor.
    const lotes = f.lotes ?? [];
    const lotesConGeometria = lotes.filter((l) => l.areaGeograficaPolygon?.coordinates?.length);
    const lotesVertices = lotesConGeometria.reduce(
      (total, l) => total + countPolygonVertices(l.areaGeograficaPolygon),
      0,
    );
    const lotesStatus = lotes.length === 0
      ? "Sin lotes registrados"
      : lotesConGeometria.length === 0
        ? "Sin geometría"
        : `${lotesConGeometria.length} lote${lotesConGeometria.length === 1 ? "" : "s"} · ${lotesVertices} vértices`;
    const lotesRow = row(
      "ph ph-shapes",
      "cl-gis-ico-kml",
      "KML Lotes",
      lotesStatus,
      `<input type="file" accept=".kml,.kmz,.zip" style="display:none" id="cl-gis-lotes-input-${f.id}">
       ${lotesConGeometria.length > 0 ? `
         <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-lotes-btn-${f.id}">
           <i class="ph ph-download-simple" aria-hidden="true"></i> Descargar
         </button>` : ""}
       <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-lotes-up-${f.id}"
         ${aoiVertices === 0 ? `disabled title="Primero cargá el AOI"` : ""}>
         <i class="ph ph-upload-simple" aria-hidden="true"></i> Cargar
       </button>`,
    );
    // Conteo: un placemark por árbol. `conteoMultiPoint` solo llega en el detalle de
    // finca. Se descarga desde ahí y se carga con `guardar-conteo`, que es síncrono.
    const conteoPuntos = f.conteoMultiPoint?.coordinates?.length ?? 0;
    const conteoRow = row(
      "ph ph-map-pin",
      "cl-gis-ico-kml",
      "KML Conteo",
      conteoPuntos > 0
        ? `${formatInt(conteoPuntos)} punto${conteoPuntos === 1 ? "" : "s"}`
        : "Sin archivo · KML con un punto por árbol",
      `<input type="file" accept=".kml" style="display:none" id="cl-gis-conteo-input-${f.id}">
       ${conteoPuntos > 0 ? `
         <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-conteo-btn-${f.id}">
           <i class="ph ph-download-simple" aria-hidden="true"></i> Descargar
         </button>` : ""}
       <button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-conteo-up-${f.id}">
         <i class="ph ph-upload-simple" aria-hidden="true"></i> Cargar
       </button>`,
    );

    // La publicación es asíncrona: el POST encola y el worker cicla cada 5 min. El
    // estado lo dicta `ortomosaicoEstado`, no la presencia del ZIP — con `uriOrtomosaico`
    // a secas, "nunca tuvo", "procesando" y "falló" se veían iguales.
    const zipRow = this._renderZipOrtomosaicoRow(f);
    // Mapa: solo tiene sentido si hay algo que mostrar (mismo criterio que las
    // filas de arriba, que ya calcularon esto) -si no, se omite la fila entera-.
    const tieneGeometria = aoiVertices > 0 || lotesConGeometria.length > 0 || conteoPuntos > 0;
    const mapaRow = tieneGeometria
      ? row(
          "ph ph-map-trifold",
          "cl-gis-ico-kml",
          "Mapa",
          "AOI, lotes y conteo sobre satélite",
          `<button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-mapa-btn-${f.id}">
             <i class="ph ph-arrows-out" aria-hidden="true"></i> Ver mapa
           </button>`,
        )
      : "";

    return `
      <div class="cl-gis-section">
        <div class="cl-gis-hd">
          <div class="cl-gis-title">Archivos GIS</div>
          <div class="cl-gis-sub">Geometrías de la finca</div>
        </div>
        ${mapaRow}
        ${aoiRow}
        ${lotesRow}
        ${conteoRow}
        ${zipRow}
      </div>
    `;
  }

  /**
   * Fila del ZIP, cuatro estados. Dos ejes independientes: **el archivo** y **su
   * publicación**. Por eso "Descargar ZIP" aparece siempre que `zipOrtomosaico` tenga
   * valor, sin mirar el estado — en ERROR es justamente cuando hace falta, para revisar
   * qué se subió. "Cargar" aparece en todos menos en PENDIENTE: mientras el worker
   * trabaja, volver a encolar solo tira a la basura el trabajo en curso.
   */
  private _renderZipOrtomosaicoRow(f: FincaFichaResponse): string {
    const descargar = f.zipOrtomosaico
      ? `<a class="sx-btn sx-btn-outline sx-btn-sm" href="${this._escapeHtml(f.zipOrtomosaico)}" target="_blank" rel="noopener">
           <i class="ph ph-download-simple" aria-hidden="true"></i> ZIP
         </a>`
      : "";
    const cargar = `<button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-zip-btn-${f.id}">
         <i class="ph ph-upload-simple" aria-hidden="true"></i> Cargar
       </button>`;

    let status: string;
    // Solo las acciones propias del estado; `descargar` se antepone después.
    let acciones: string;
    switch (f.ortomosaicoEstado) {
      case OrtomosaicoEstado.PENDIENTE:
        status   = "Procesando · disponible en unos minutos";
        acciones = "";
        break;
      case OrtomosaicoEstado.LISTO:
        status   = "Publicado";
        acciones = cargar;
        break;
      case OrtomosaicoEstado.ERROR:
        status = `Error: ${this._escapeHtml(f.ortomosaicoError ?? "sin detalle")}`;
        // Reintentar re-encola el MISMO zip que ya está en el blob: no hay que volver a subirlo.
        acciones = `<button class="sx-btn sx-btn-outline sx-btn-sm" id="cl-gis-zip-retry-${f.id}">
                      <i class="ph ph-arrow-clockwise" aria-hidden="true"></i> Reintentar
                    </button>` + cargar;
        break;
      default:
        status   = f.zipOrtomosaico
          ? "Sin publicar"
          : "Sin archivo · ZIP con pirámide de tiles ({z}/{x}/{y}.png)";
        acciones = cargar;
    }

    // El ZIP se puede bajar siempre que exista, en cualquiera de los cuatro estados.
    acciones = descargar + acciones;

    const statusCls = f.ortomosaicoEstado === OrtomosaicoEstado.ERROR
      ? "cl-gis-file-status cl-gis-status-err"
      : f.ortomosaicoEstado === OrtomosaicoEstado.LISTO
        ? "cl-gis-file-status cl-gis-status-ok"
        : "cl-gis-file-status";

    return `
      <div class="cl-gis-file" id="cl-gis-zip-row-${f.id}">
        <div class="cl-gis-file-ico cl-gis-ico-zip"><i class="ph ph-file-zip" aria-hidden="true"></i></div>
        <div class="cl-gis-file-info">
          <div class="cl-gis-file-name">ZIP Ortomosaico</div>
          <div class="${statusCls}" id="cl-gis-zip-status-${f.id}">${status}</div>
        </div>
        <input type="file" accept=".zip" style="display:none" id="cl-gis-zip-input-${f.id}">
        ${acciones}
      </div>
    `;
  }

  private _wireGisUploads(f: FincaFichaResponse, c: ClienteResponse): void {
    const mapaBtn = document.getElementById(`cl-gis-mapa-btn-${f.id}`) as HTMLButtonElement | null;
    if (mapaBtn) mapaBtn.onclick = () => new FincaGisMapModal(f).init();

    const aoiBtn = document.getElementById(`cl-gis-aoi-btn-${f.id}`) as HTMLButtonElement | null;
    if (aoiBtn) aoiBtn.onclick = () => this._onDownloadAoi(f);

    const lotesBtn = document.getElementById(`cl-gis-lotes-btn-${f.id}`) as HTMLButtonElement | null;
    if (lotesBtn) lotesBtn.onclick = () => this._onDownloadLotes(f);

    const conteoBtn = document.getElementById(`cl-gis-conteo-btn-${f.id}`) as HTMLButtonElement | null;
    if (conteoBtn) conteoBtn.onclick = () => this._onDownloadConteo(f);

    const conteoUp    = document.getElementById(`cl-gis-conteo-up-${f.id}`) as HTMLButtonElement | null;
    const conteoInput = document.getElementById(`cl-gis-conteo-input-${f.id}`) as HTMLInputElement | null;
    if (conteoUp && conteoInput) {
      conteoUp.onclick = () => conteoInput.click();
      conteoInput.onchange = () => {
        const file = conteoInput.files?.[0];
        if (file) void this._onUploadConteo(f, c, file);
      };
    }

    const aoiUp    = document.getElementById(`cl-gis-aoi-up-${f.id}`) as HTMLButtonElement | null;
    const aoiInput = document.getElementById(`cl-gis-aoi-input-${f.id}`) as HTMLInputElement | null;
    if (aoiUp && aoiInput) {
      aoiUp.onclick = () => aoiInput.click();
      aoiInput.onchange = () => {
        const file = aoiInput.files?.[0];
        if (file) void this._onUploadAoi(f, c, file);
      };
    }

    const lotesUp    = document.getElementById(`cl-gis-lotes-up-${f.id}`) as HTMLButtonElement | null;
    const lotesInput = document.getElementById(`cl-gis-lotes-input-${f.id}`) as HTMLInputElement | null;
    if (lotesUp && lotesInput) {
      lotesUp.onclick = () => lotesInput.click();
      lotesInput.onchange = () => {
        const file = lotesInput.files?.[0];
        if (file) void this._onUploadLotes(f, c, file);
      };
    }

    const retryBtn = document.getElementById(`cl-gis-zip-retry-${f.id}`) as HTMLButtonElement | null;
    if (retryBtn && f.zipOrtomosaico) {
      retryBtn.onclick = () => void this._encolarOrtomosaico(f, c, f.zipOrtomosaico!);
    }

    const btn   = document.getElementById(`cl-gis-zip-btn-${f.id}`) as HTMLButtonElement | null;
    const input = document.getElementById(`cl-gis-zip-input-${f.id}`) as HTMLInputElement | null;
    if (!btn || !input) return;

    // Encolar descarta el ortomosaico publicado: se avisa antes de abrir el explorador.
    btn.onclick = () => {
      if (f.ortomosaicoEstado !== OrtomosaicoEstado.LISTO) { input.click(); return; }
      new ConfirmDialogComponent({
        title: "Reemplazar ortomosaico",
        message: "El ortomosaico actual se descarta al empezar. La finca queda sin capa hasta que termine de procesar, y eso tarda unos minutos.",
        confirmLabel: "Reemplazar",
        onConfirm: () => input.click(),
      }).init();
    };
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void this._onUploadZip(f, c, file);
    };
  }

  private _onDownloadConteo(f: FincaFichaResponse): void {
    const conteo = f.conteoMultiPoint;
    if (!conteo?.coordinates?.length) {
      errorMessageComponent.addError("Sin geometría", "Esta finca no tiene puntos de conteo registrados.");
      return;
    }
    downloadKml(kmlFileName("Conteo", f.nombreFinca), multiPointToKml(`Conteo ${f.nombreFinca}`, conteo));
  }

  private _onDownloadAoi(f: FincaFichaResponse): void {
    const aoi = f.sateliteAreaGeografica as Polygon | null | undefined;
    if (!aoi?.coordinates?.length) {
      errorMessageComponent.addError("Sin geometría", "Esta finca no tiene área de interés registrada.");
      return;
    }
    const nombre = `AOI ${f.nombreFinca}`;
    downloadKml(kmlFileName("AOI", f.nombreFinca), polygonToKml(nombre, aoi));
  }

  // Números crudos y con punto decimal: con separador local QGIS no puede castear
  // la columna a numérico. La unidad la aporta la etiqueta del atributo.
  private _kmlNum(value: number | string | null | undefined): string {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : "";
  }

  private _onDownloadLotes(f: FincaFichaResponse): void {
    const items = (f.lotes ?? [])
      .filter((l) => l.areaGeograficaPolygon?.coordinates?.length)
      .map((l) => ({
        name: l.nombreLote,
        polygon: l.areaGeograficaPolygon,
        // Las cuatro claves se emiten siempre, aunque el valor quede vacío: si unos
        // placemarks las traen y otros no, la tabla de atributos sale irregular.
        fields: [
          { name: "areaHectareas",    label: "Área (ha)",       value: this._kmlNum(l.areaHectareas) },
          { name: "areaCuadras",      label: "Área (cuadras)",  value: this._kmlNum(l.areaCuadras) },
          { name: "isAreaProductiva", label: "Área productiva", value: l.isAreaProductiva ? "Sí" : "No" },
          { name: "cultivo",          label: "Cultivo",         value: l.cultivo?.nombre ?? "" },
        ],
      }));
    if (items.length === 0) {
      errorMessageComponent.addError("Sin geometría", "Esta finca no tiene lotes con área registrada.");
      return;
    }
    downloadKml(kmlFileName("Lotes", f.nombreFinca), polygonsToKml(`Lotes ${f.nombreFinca}`, items));
  }

  /**
   * Sube el archivo a Azure y devuelve su blobUrl. El ZIP del ortomosaico y el KML del
   * conteo comparten el flujo (`GET /storage/sas-url` → PUT directo al blob, sin pasar
   * por la API) y solo difieren en el content-type.
   */
  private async _subirABlob(file: File, contentType: string): Promise<string> {
    const { sasUrl, blobUrl } = await storageSubidaService.getSasSubida(file.name);
    const resp = await fetch(sasUrl, {
      method: "PUT",
      headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType },
      body: file,
    });
    if (!resp.ok) throw new Error(`Azure upload falló: ${resp.status}`);
    return blobUrl;
  }

  /**
   * `guardar-conteo` es síncrono: parsea el KML y responde con la finca. Como la
   * respuesta es la finca *base* y `conteoMultiPoint` solo viaja en el detalle, hay
   * que recargar la ficha para repintar la fila con la cantidad de puntos real.
   */
  private async _onUploadConteo(f: FincaFichaResponse, c: ClienteResponse, file: File): Promise<void> {
    // El spinner del botón es el único indicador: la fila la arma el helper `row()`,
    // que no expone un id para el texto de estado.
    const btn = document.getElementById(`cl-gis-conteo-up-${f.id}`) as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<i class="ph ph-circle-notch cl-spin" aria-hidden="true"></i>';
    }

    try {
      const uriConteo = await this._subirABlob(file, "application/vnd.google-earth.kml+xml");
      await GuardarConteoService.guardar({ fincaId: f.id, uriConteo });
    } catch (err) {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<i class="ph ph-upload-simple" aria-hidden="true"></i> Reintentar';
      }
      errorMessageComponent.addError("Error al subir el conteo", (err as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("Conteo cargado", `Se actualizó el conteo de ${f.nombreFinca}.`);
    await this._goToFincaDetail(f, c);
  }

  // .kmz/.zip van como application/zip; un .kml plano, como KML. El content-type
  // solo es metadata del blob en Azure — el parser del servidor detecta el
  // formato real por el contenido, no por este header.
  private _contentTypeKml(file: File): string {
    return /\.(kmz|zip)$/i.test(file.name)
      ? "application/zip"
      : "application/vnd.google-earth.kml+xml";
  }

  private async _onUploadAoi(f: FincaFichaResponse, c: ClienteResponse, file: File): Promise<void> {
    const btn = document.getElementById(`cl-gis-aoi-up-${f.id}`) as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<i class="ph ph-circle-notch cl-spin" aria-hidden="true"></i>';
    }

    try {
      const uriKml = await this._subirABlob(file, this._contentTypeKml(file));
      await GuardarAoiService.guardar({ fincaId: f.id, uriKml, clienteId: c.id });
    } catch (err) {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<i class="ph ph-upload-simple" aria-hidden="true"></i> Reintentar';
      }
      errorMessageComponent.addError("Error al cargar el AOI", (err as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("AOI cargado", `Se actualizó el área de interés de ${f.nombreFinca}.`);
    await this._goToFincaDetail(f, c);
  }

  private async _onUploadLotes(f: FincaFichaResponse, c: ClienteResponse, file: File): Promise<void> {
    const btn = document.getElementById(`cl-gis-lotes-up-${f.id}`) as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<i class="ph ph-circle-notch cl-spin" aria-hidden="true"></i>';
    }

    try {
      const uriKml = await this._subirABlob(file, this._contentTypeKml(file));
      await GuardarLotesService.guardar({ fincaId: f.id, uriKml });
    } catch (err) {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<i class="ph ph-upload-simple" aria-hidden="true"></i> Reintentar';
      }
      errorMessageComponent.addError("Error al cargar los lotes", (err as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("Lotes cargados", `Se actualizaron los lotes de ${f.nombreFinca}.`);
    await this._goToFincaDetail(f, c);
  }

  private async _onUploadZip(f: FincaFichaResponse, c: ClienteResponse, file: File): Promise<void> {
    const btn    = document.getElementById(`cl-gis-zip-btn-${f.id}`) as HTMLButtonElement | null;
    const status = document.getElementById(`cl-gis-zip-status-${f.id}`);
    if (!btn || !status) return;

    btn.disabled = true;
    btn.innerHTML = '<i class="ph ph-circle-notch cl-spin" aria-hidden="true"></i>';
    status.textContent = "Subiendo...";
    status.className = "cl-gis-file-status";

    try {
      const blobUrl = await this._subirABlob(file, "application/zip");
      await this._encolarOrtomosaico(f, c, blobUrl);
    } catch (err) {
      status.textContent = "Error al subir";
      status.className = "cl-gis-file-status cl-gis-status-err";
      btn.innerHTML = '<i class="ph ph-upload-simple" aria-hidden="true"></i> Reintentar';
      btn.disabled = false;
      errorMessageComponent.addError("Error al subir ortomosaico", (err as Error).message);
    }
  }

  /**
   * Encola la publicación (subida nueva o reintento de una fallida) y recarga la ficha:
   * el estado real lo dicta el servidor, no lo pintamos a mano. `start()` despierta al
   * vigilante, que avisa por toast cuando el worker termine.
   */
  private async _encolarOrtomosaico(f: FincaFichaResponse, c: ClienteResponse, zipUrl: string): Promise<void> {
    try {
      await GuardarOrtomosaicoService.guardar({ fincaId: f.id, zipOrtomosaico: zipUrl });
    } catch (err) {
      errorMessageComponent.addError("No se pudo encolar el ortomosaico", (err as Error).message);
      return;
    }
    OrtomosaicoWatchService.instance.start();
    errorMessageComponent.addSuccess(
      "Ortomosaico en proceso",
      "Te avisamos cuando esté publicado. Suele tardar unos minutos.",
    );
    await this._goToFincaDetail(f, c);
  }

  private _renderTipoMonitoreo(tipo: string | null): string {
    if (!tipo) return `<span class="cl-mon-badge cl-mon-na">Sin monitoreo</span>`;
    return `<span class="cl-mon-badge">${this._escapeHtml(tipo)}</span>`;
  }

  // ── Actions ──

  private _onCreateClick(): void {
    new ClienteFormModal((saved) => {
      this._clientes = [saved, ...this._clientes];
      this._filterAndRender();
    }).init();
  }

  private _onEditClick(item: ClienteResponse): void {
    new ClienteFormModal((saved) => {
      this._clientes = this._clientes.map((c) => (c.id === saved.id ? saved : c));
      this._filterAndRender();
      this._renderDetail({ ...saved, fincas: item.fincas });
    }, item).init();
  }

  /**
   * Edita la finca desde su ficha: al guardar reemplaza la card en el detalle del cliente con el resumen
   * devuelto y vuelve a pedir la ficha (el servidor recalcula nombre, ubicación y cultivos).
   */
  private _onEditFincaClick(f: FincaFichaResponse, c: ClienteResponse): void {
    new FincaFormModal(c.id, (saved) => {
      const updatedFincas = (c.fincas ?? []).map((fi) => (fi.id === saved.id ? saved : fi));
      const updatedCliente: ClienteResponse = { ...c, fincas: updatedFincas };
      void this._goToFincaDetail(saved, updatedCliente);
    }, f).init();
  }

  /**
   * Ojo: el DELETE da de baja la relación cliente-finca, no la finca — si tiene otro
   * dueño, le sigue apareciendo. La UI dice "Eliminar" por decisión de producto.
   */
  private _onDeleteFincaClick(f: FincaFichaResponse, c: ClienteResponse): void {
    new ConfirmDialogComponent({
      title: "Eliminar finca",
      message: `¿Eliminar la finca ${this._escapeHtml(f.nombreFinca)}?`,
      confirmLabel: "Eliminar",
      onConfirm: () => void this._removeFinca(f, c),
    }).init();
  }

  private async _removeFinca(f: FincaFichaResponse, c: ClienteResponse): Promise<void> {
    try {
      await FincaDeleteService.instance.delete(f.id, c.id);
    } catch (error) {
      errorMessageComponent.addError("No se pudo eliminar la finca", (error as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("Finca eliminada", `${f.nombreFinca} ya no está asociada a ${c.nombre}.`);
    // Recarga el detalle en vez de parchear la lista en memoria: `cantFincas` y
    // `hectareasTotales` los calcula el servidor y no sabemos recomponerlos acá.
    await this._goToDetail(c);
  }

  private _onNewFincaClick(c: ClienteResponse): void {
    new FincaFormModal(c.id, (finca) => {
      const updatedFincas = [...(c.fincas ?? []), finca];
      const updatedCliente: ClienteResponse = {
        ...c,
        fincas: updatedFincas,
        cantFincas: (c.cantFincas ?? 0) + 1,
      };
      this._clientes = this._clientes.map((cl) =>
        cl.id === c.id ? { ...cl, cantFincas: updatedCliente.cantFincas } : cl,
      );
      this._renderDetail(updatedCliente);
    }).init();
  }

  // ── Utils ──

  private _escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  private _emptyState(icon: string, title: string, desc: string): string {
    return `
      <div class="cl-empty-state">
        <i class="${icon}" aria-hidden="true"></i>
        <div class="cl-empty-title">${title}</div>
        <div class="cl-empty-desc">${desc}</div>
      </div>
    `;
  }
}
